import { useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action, Dispatch } from 'redux';

import { CardTransferStatusSuccessParam } from '../../../lib/models/payment/types';
import { RootState } from '../../../lib/store';
import { setPendingPurchasedPlanId } from '../../../lib/store/crm';
import { getCardTransferStatus } from '../../../lib/store/payment';
import { PaymentCall } from '../../../lib/store/payment/types';
import {
  isCallStatusError,
  isCallStatusInitial,
} from '../../../lib/util/error-handling/StatusByCallHelpers';
import {
  getPaymentProvider,
  getPaymentReferenceId,
  getPurchasedPlanId,
  isPlanWithAutoPassPurchasePaymentCallback,
  markPlanPurchasePaymentCallback,
} from '../../../lib/util/url-helpers';

import { Preloader } from '../../../components';
import { GTM_CATEGORIES, GTM_EVENTS, GTM_PURCHASE } from '../../../constants/gtm';
import { hashSHA256 } from '../../../util/hash';
import { UI_ROUTES } from '../../../util/routes';

type Props = ReduxProps;

const PaymentPlanSuccess = (props: Props) => {
  const {
    storePendingPurchasedPlanId,
    getPlanPaymentStatus,
    isCardTransferStatusInitial,
    isCardTransferStatusError,
    userId,
    userEmail,
    tradingPlans,
  } = props;

  const hasTrackedPurchase = useRef<boolean>(false);

  const navigate = useNavigate();
  const purchasedPlanId = getPurchasedPlanId();
  const paymentReferenceId = getPaymentReferenceId();
  const paymentProvider = getPaymentProvider();
  const withAutoPass = isPlanWithAutoPassPurchasePaymentCallback();

  useEffect(() => {
    // Store the pending purchased plan ID immediately
    if (purchasedPlanId) {
      storePendingPurchasedPlanId(purchasedPlanId);
    }

    // Trigger payment status check
    if (
      paymentReferenceId
      && paymentProvider
      && isCardTransferStatusInitial
      && !isCardTransferStatusError
    ) {
      getPlanPaymentStatus({ paymentReference: paymentReferenceId, paymentProvider });
    }

    // GTM: Track successful payment (fire only once)
    if (!hasTrackedPurchase.current && purchasedPlanId) {
      hasTrackedPurchase.current = true;
      const plan = tradingPlans.find((p) => p.id === purchasedPlanId);
      const planPrice = plan?.plan_price ?? 0;
      const planPriceWithAutoPass = plan?.plan_price_with_auto_pass ?? 0;
      const price = withAutoPass ? planPriceWithAutoPass : planPrice;
      const pushPurchaseEvent = async () => {
        const hashedEmail = await hashSHA256(userEmail || '');
        TagManager.dataLayer({
          dataLayer: {
            event: GTM_EVENTS.PURCHASE_COMPLETED,
            event_category: GTM_CATEGORIES.CHALLENGE_PURCHASE,
            event_action: GTM_PURCHASE.ACTION,
            event_label: GTM_PURCHASE.LABEL,
            client_id: userId,
            user_email_hashed: hashedEmail,
            transaction_id: paymentReferenceId,
            challenge_name: plan?.phase,
            buying_power: plan?.daily_hard_bp,
            currency: plan?.daily_hard_ccy,
            voucher_code: 'N/A',
            voucher_amount: 0,
            price_before_voucher: price,
            paid_price: price,
          },
        });
      };
      pushPurchaseEvent();
    }

    // Redirect to overview after a brief delay to ensure state update
    const redirectTimer = setTimeout(() => {
      const isPlanCallback = markPlanPurchasePaymentCallback();
      navigate(UI_ROUTES.overview, { state: { isPlanCallback } });
    }, 100);

    return () => clearTimeout(redirectTimer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ border: 'none' }}>
      <Preloader isFullScreen text="Processing payment..." owner="PaymentPlanSuccess" />
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  isCardTransferStatusInitial: isCallStatusInitial(
    PaymentCall.getCardTransferStatus,
    state.payment.statusByCall,
  ),
  isCardTransferStatusError: isCallStatusError(
    PaymentCall.getCardTransferStatus,
    state.payment.statusByCall,
  ),
  userId: state.auth.enroll.userId,
  userEmail: state.crm.individualExtendedInfo?.email,
  tradingPlans: state.crm.tradingPlans,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  storePendingPurchasedPlanId: (planId: number | null) =>
    dispatch(setPendingPurchasedPlanId(planId)),
  getPlanPaymentStatus: (payload: CardTransferStatusSuccessParam) =>
    dispatch(getCardTransferStatus(payload)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(PaymentPlanSuccess);
